import React, { useEffect } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";

import { ACTIONS, BOOKINGS_TABS, BOOKINGS_VIEWS, URL } from "../Bookings";
import LogsTitle from "../Sections/LogsTitle";
import BookingsList from "../Components/BookingsList";
import {
    getBookingListData,
    getBookingListRefreshData,
    getInitialData
} from "../Requests/BookingListRequests";
import { useInterval } from "../../../Hooks/useInterval";
import EditBookingModal from "../Components/Modals/EditBookingModal";
import PageNotFound from "../../../Components/PageNotFound";
import ErrorMessage from "../../../Components/ErrorMessage";
import { categoryFilterChanged, searchTermChanged } from "../Redux/FiltersReducer";
import { elapsedBookingsRemoved } from "../Redux/BookingListSlice";
import BookingsLogUrlActions from "./BookingsLogUrlActions";
import BookingsLogsOnboarding from "../Onboarding/BookingsLogsOnboarding";
import BookingsTotalsBar from "./BookingsTotalsBar";

export const MODAL_EDIT_BOOKING = 'edit-booking';
export const MODAL_CANCEL_BOOKING = 'cancel-booking';
export const MODAL_DELETE_BOOKING = 'delete-booking';

export const TabContext = React.createContext({})

/**
 * Returns true if any filters are set which should prevent the
 * automatic removal of elapsed bookings
 * @param filters
 * @returns {boolean}
 */
const anyFiltersSet = (filters) => {
    if (filters.status && filters.status !== 'all') {
        return true;
    }
    if (filters.dateRangeEnd || filters.dateRangeStart) {
        return true;
    }
}

const BookingsLog = (props) => {
    const dispatch = useDispatch();
    const {view, scope, categorySlug} = useParams();

    if (![BOOKINGS_VIEWS.MY_BOOKINGS, BOOKINGS_VIEWS.ALL_BOOKINGS].includes(view)
        || ![BOOKINGS_TABS.UPCOMING, BOOKINGS_TABS.PAST].includes(scope)
    ) {
        return <PageNotFound/>;
    }

    const permissions = useSelector(state => state.module.BOOKINGS.common.permissions);

    const filters = useSelector(state => state.module.BOOKINGS.filters);
    const bookings = useSelector(state => state.module.BOOKINGS.bookingList[view][scope].bookings);
    const dataLoadCount = useSelector(state => state.module.BOOKINGS.bookingList[view][scope].dataLoadCount);

    if (!['all-bookings', 'my-bookings'].includes(view)) {
        return null;
    }

    /**
     * Run an update action every 5 seconds
     * to remove any bookings that have become invalid for this tab/scope
     * from the redux state.
     * Only relevant for upcoming tab at the moment
     * @todo Would be good to change this so it doesn't spam a redux action every 5 seconds that mostly does nothing
     */
    useInterval(() =>
        {
            if (!dataLoadCount) {
                return;
            }

            if (scope === BOOKINGS_TABS.UPCOMING && !anyFiltersSet(filters)) {
                dispatch( elapsedBookingsRemoved(view, scope) );
            }
        },
        15000
    );

    /**
     * Run an update every minute to request any changes
     * from the backend
     */
    useInterval(
        () => {
            dispatch( getBookingListRefreshData(view, scope) )
        },
        60000
    );

    // Get booking data on first load
    useEffect(() => {
        if (scope === BOOKINGS_TABS.UPCOMING && dataLoadCount === 0) {
            dispatch(getInitialData(scope, view, categorySlug));
        } else {
            dispatch(getBookingListData(scope, view, false));
        }
    }, [view, scope]);

    // Update the category in the state when the category slug in the url
    // changes. Probably on arrival from a url, but also after the
    // filter dropdown changes
    useEffect(() => {
        dispatch( categoryFilterChanged(categorySlug) );
    }, [categorySlug])

    // Get booking data when filters change
    useEffect(() => {
        if (dataLoadCount > 0) {
            dispatch(getBookingListData(scope, view, true));
        }
    }, [ filters ]);

    if (!permissions) {
        return null;
    }

    if (view === BOOKINGS_VIEWS.ALL_BOOKINGS && !(permissions.view_user_prop || permissions.view_all)) {
        return <ErrorMessage url="/"/>;
    }

    return (<>
        <LogsTitle view={view} scope={scope}/>

        <BookingsTotalsBar view={view} scope={scope}/>

        <Tabs value={scope} variant="fullWidth">
            <Tab
                label="Upcoming"
                value={`${BOOKINGS_TABS.UPCOMING}`}
                to={`/bookings/logs/${view}/${BOOKINGS_TABS.UPCOMING}`}
                component={Link}
                disableRipple
                disableFocusRipple
            />
            <Tab
                label="Past"
                value={`${BOOKINGS_TABS.PAST}`}
                to={`/bookings/logs/${view}/${BOOKINGS_TABS.PAST}`}
                component={Link}
                disableRipple
                disableFocusRipple
            />
        </Tabs>

        <TabContext.Provider value={{view, scope}}>
            <BookingsList
                rows={bookings}
                isTimeResponsive={(scope === BOOKINGS_TABS.UPCOMING)}
                groupByDate
            />

            { (scope === BOOKINGS_TABS.UPCOMING) &&
                <EditBookingModal/>
            }

            <BookingsLogUrlActions/>
        </TabContext.Provider>

        <BookingsLogsOnboarding/>
    </>);
}

export default BookingsLog;
